import type { FC } from 'react'
import { AlertTriangle, AlertCircle, Info, HeartPulse } from 'lucide-react'
import type { DashboardKpi } from '../core/dashboard-types'
import CardInfoButton from './CardInfoButton'

const healthBarMeta = {
  description: 'Aggregated fleet health KPIs including active risks, unresolved warnings, pending info items, and overall fleet health percentage.',
  endpoint: 'GET /pms/command-center-stats + GET /qhse/defects/count + GET /pms/jobs/search?state=OVERDUE',
  logic: 'Each KPI value is computed from PMS stats, defect counts, and job statuses. Fleet health percentage = ratio of healthy vessels to total fleet.',
}

type Props = {
  kpis: DashboardKpi[]
  onKpiClick?: (kpi: DashboardKpi) => void
}

const iconMap = {
  risks: AlertTriangle,
  warnings: AlertCircle,
  info: Info,
  health: HeartPulse,
} as const

const colorMap: Record<DashboardKpi['color'], { bg: string; text: string }> = {
  red: { bg: 'bg-red-50', text: 'text-red-600' },
  amber: { bg: 'bg-amber-50', text: 'text-amber-600' },
  blue: { bg: 'bg-blue-50', text: 'text-blue-600' },
  emerald: { bg: 'bg-emerald-50', text: 'text-emerald-600' },
}

const numericColorMap: Record<DashboardKpi['color'], string> = {
  red: 'text-red-600',
  amber: 'text-amber-600',
  blue: 'text-blue-600',
  emerald: 'text-emerald-600',
}

const FleetHealthBar: FC<Props> = ({ kpis, onKpiClick }) => {
  if (!kpis.length) return null

  return (
    <div className='relative'>
      <div className='absolute top-2 right-3 z-10'>
        <CardInfoButton meta={healthBarMeta} />
      </div>
      <div className='bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden'>
        <div className='flex items-stretch overflow-x-auto md:flex-wrap'>
          {kpis.map((kpi, i) => {
            const Icon = iconMap[kpi.icon]
            const iconStyle = colorMap[kpi.color]
            const valColor = numericColorMap[kpi.color]

            return (
              <div
                key={i}
                className={`flex items-center gap-3 px-4 md:px-5 py-3 flex-shrink-0 min-w-[200px] cursor-pointer hover:bg-slate-50 transition-colors ${i < kpis.length - 1 ? 'border-r border-slate-100' : ''}`}
                onClick={() => onKpiClick?.(kpi)}
                role='button'
                tabIndex={0}
                onKeyDown={(e) => { if (e.key === 'Enter') onKpiClick?.(kpi) }}
              >
                <div className={`w-10 h-10 rounded-lg ${iconStyle.bg} flex items-center justify-center flex-shrink-0`}>
                  <Icon className={`w-5 h-5 ${iconStyle.text}`} />
                </div>
                <div>
                  <div className='flex items-baseline gap-1.5'>
                    <span className={`text-xl font-bold ${valColor}`}>{kpi.value}{kpi.icon === 'health' ? '%' : ''}</span>
                    <span className='text-sm font-semibold text-slate-700'>{kpi.label}</span>
                  </div>
                  <p className='text-[11px] text-slate-500'>{kpi.subtitle}</p>
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}

export default FleetHealthBar
